import { useToast } from "@ligo/layout-design-core";
import { Errors } from "classes";
import { allFormsKey, ss } from "consts";
import { FormFieldModel } from "models";
import {
  AttBaseProviderType,
  PermissionAttProviderType,
  useAttendDetail,
} from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as AttendanceService from "services/attendance";
import { aytyFormatError } from "utils";

type FormFieldContextType = {
  loading: boolean;
  formFields?: FormFieldModel[];
  getForms: () => Promise<void>;
  getFormDataByFields: () => FormData;
  updateFormFields: (form: FormFieldModel[]) => void;
};

const FormFieldContext = createContext({} as FormFieldContextType);

const Provider = ({ children, all }: AttBaseProviderType) => {
  const [loading, setLoading] = useState(true);
  const [formFields, setFormFields] = useState<FormFieldModel[]>();
  const { pIdOmni } = useAttendDetail();
  const { error, warning } = useToast();
  const { t } = useTranslation();

  const errorsResolver = useMemo(
    () => new Errors({ error, warning }, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const list = useMemo(
    () =>
      formFields?.sort((a, b) => {
        if (a.NuOrder > b.NuOrder) return 1;
        else if (a.NuOrder < b.NuOrder) return -1;
        return 0;
      }),
    [formFields],
  );

  useEffect(() => {
    if (pIdOmni) {
      setLoading(true);
      setFormFields(undefined);
    }
  }, [pIdOmni]);

  const getForms = useCallback(async () => {
    let current: FormFieldModel[] = all[pIdOmni];

    if (!current)
      await AttendanceService.getFormFields({ pIdOmni })
        .then(async ({ data }) => {
          if (data.idReturnAPI > 0) {
            current = data.bindListCustomer;
            all[pIdOmni] = data.bindListCustomer;
          } else warning({ description: t(aytyFormatError(data)) });
        })
        .catch(errorsResolver.defaultError);

    setFormFields(current);
    ss.setItem(allFormsKey, JSON.stringify(all));

    setLoading(false);
  }, [all, pIdOmni, errorsResolver, warning, t]);

  useEffect(() => {
    getForms();
  }, [getForms]);

  const getFormDataByFields = useCallback(() => {
    const formData = new FormData();

    formFields?.forEach((field) => {
      formData.append(field.NmControl, field.VlProperty?.toString());
    });

    return formData;
  }, [formFields]);

  const updateFormFields = useCallback(
    (form: FormFieldModel[]) => {
      all[pIdOmni] = form;
      setFormFields([...form]);
      ss.setItem(allFormsKey, JSON.stringify(all));
    },
    [all, pIdOmni],
  );

  return (
    <FormFieldContext.Provider
      value={{
        loading,
        formFields: list,
        getForms,
        getFormDataByFields,
        updateFormFields,
      }}
    >
      {children}
    </FormFieldContext.Provider>
  );
};

export const FormFieldProvider = ({
  hasPermission = true,
  ...args
}: PermissionAttProviderType) => {
  if (!hasPermission) return <>{args.children}</>;

  return <Provider {...args} />;
};

export const useFormField = () => useContext(FormFieldContext);
